import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BreadcrumbService } from 'xng-breadcrumb';
import {
  CountryService,
  ExamService,
  FormUtilsService,
  NotificationService,
  UniversityService,
} from '../../../services';
import {
  FormValidationErrorResponse,
  SelectOption,
  UniversityResponse,
} from '../../../types';

@Component({
  selector: 'app-university-form',
  templateUrl: './university-form.component.html',
})
export class UniversityFormComponent implements OnInit {
  readonly translationKeyPrefix = 'university.';
  translationKeyErrorMessages = {
    pattern: 'label.university.translation-key-error',
  };
  autoSendErrorMessages = {
    autoSendNotInApplications:
      'label.university.test-templates-auto-send-error',
  };
  private form!: FormGroup;

  constructor(
    private breadcrumbService: BreadcrumbService,
    private countryService: CountryService,
    private examService: ExamService,
    private formBuilder: FormBuilder,
    private formUtils: FormUtilsService,
    private logger: NGXLogger,
    private note: NotificationService,
    private router: Router,
    private service: UniversityService
  ) {}

  ngOnInit(): void {
    this.examService.loadTemplateOptions();
    const applicationsControl = this.formBuilder.control(
      this.item.testTemplatesForApplications || []
    );
    const autoSendControl = this.formBuilder.control(
      this.item.testTemplatesForAutoSend || [],
      [this.autoSendSubsetValidator()]
    );

    this.form = this.formBuilder.group({
      translationKey: [
        this.removeTranslationKeyPrefix(
          this.item.translationKey
        ),
        [
          Validators.required,
          Validators.pattern(/^[a-z0-9-]+$/),
        ],
      ],
      nameDe: [this.item.names.de, Validators.required],
      nameEn: [this.item.names.en, Validators.required],
      country: [this.item.country, Validators.required],
      description: [this.item.description],
      testTemplatesForApplications: applicationsControl,
      testTemplatesForAutoSend: autoSendControl,
    });
    autoSendControl.updateValueAndValidity();
    applicationsControl.valueChanges.subscribe(() =>
      autoSendControl.updateValueAndValidity()
    );

    if (!this.service.isNew) {
      this.translationKeyControl.disable();
    }

    this.setBreadcrumb(this.nameDeControl.value);
    this.nameDeControl.valueChanges.subscribe((name) =>
      this.setBreadcrumb(name)
    );
  }

  get item(): UniversityResponse {
    return this.service.formState.item;
  }

  get countryOptions(): SelectOption[] {
    return this.countryService.selectOptions;
  }

  get templateOptions(): SelectOption[] {
    return this.examService.templateSelectOptions;
  }

  get translationKeyControl(): FormControl<string | null> {
    return this.form.get('translationKey') as FormControl<
      string | null
    >;
  }

  get nameDeControl(): FormControl<string | null> {
    return this.form.get('nameDe') as FormControl<
      string | null
    >;
  }

  get nameEnControl(): FormControl<string | null> {
    return this.form.get('nameEn') as FormControl<
      string | null
    >;
  }

  get countryControl(): FormControl<string | null> {
    return this.form.get('country') as FormControl<
      string | null
    >;
  }

  get descriptionControl(): FormControl<string | null> {
    return this.form.get('description') as FormControl<
      string | null
    >;
  }

  get applicationsControl(): FormControl<string[] | null> {
    return this.form.get(
      'testTemplatesForApplications'
    ) as FormControl<string[] | null>;
  }

  get autoSendControl(): FormControl<string[] | null> {
    return this.form.get(
      'testTemplatesForAutoSend'
    ) as FormControl<string[] | null>;
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.item.translationKey = `${
      this.translationKeyPrefix
    }${this.translationKeyControl.value ?? ''}`;
    this.item.names = {
      de: this.nameDeControl.value ?? '',
      en: this.nameEnControl.value ?? '',
    };
    this.item.country =
      this.formUtils.unwrapSingleSelect<string>(
        this.countryControl.value
      ) ?? '';
    this.item.description =
      this.descriptionControl.value || '';
    this.item.testTemplatesForApplications =
      this.applicationsControl.value || [];
    this.item.testTemplatesForAutoSend =
      this.autoSendControl.value || [];

    this.logger.debug('save university', {
      item: this.item,
    });
    this.service
      .save()
      .then(() =>
        this.router.navigate(['/home/university'])
      )
      .catch((errors: FormValidationErrorResponse) =>
        this.fail(errors)
      );
  }

  private autoSendSubsetValidator(): ValidatorFn {
    return (
      control: AbstractControl
    ): ValidationErrors | null => {
      const applications =
        (control.parent?.get('testTemplatesForApplications')
          ?.value as string[] | null) ??
        this.item.testTemplatesForApplications;
      const autoSend =
        (control.value as string[] | null) ?? [];

      return autoSend.every((template) =>
        applications.includes(template)
      )
        ? null
        : { autoSendNotInApplications: true };
    };
  }

  private removeTranslationKeyPrefix(
    translationKey: string
  ): string {
    return translationKey.startsWith(
      this.translationKeyPrefix
    )
      ? translationKey.slice(
          this.translationKeyPrefix.length
        )
      : translationKey;
  }

  private setBreadcrumb(name: string | null): void {
    this.breadcrumbService.set('@universityName', {
      label: name ?? '',
      skip: !name,
    });
  }

  private fail(errors: FormValidationErrorResponse): void {
    if (!Array.isArray(errors)) {
      this.note.error('label.university.save-error');
      return;
    }
    errors.forEach((error) => {
      const field = this.form.get(error.field);
      field?.setErrors({ custom: error.message });
    });
  }
}
